/**
 * Auth utilities for API calls
 * Handles both cookie-based and localStorage token authentication,
 * plus SEC-003 CSRF double-submit tokens for cookie-authenticated mutations.
 *
 * SECURITY NOTE: the session token is intentionally mirrored in localStorage
 * as a fallback because httpOnly session cookies do not persist reliably in
 * every deployment environment (cross-site contexts / some mobile browsers).
 * The cookie remains the primary mechanism; tokens are short-lived server-side
 * sessions that can be revoked, and the app renders no untrusted HTML
 * (no dangerouslySetInnerHTML), which limits XSS exposure.
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const UNSAFE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/**
 * Get auth headers - uses localStorage token as fallback when cookies fail
 * This is needed for production environments where cookies may not persist
 */
export function getAuthHeaders() {
  const token = localStorage.getItem("session_token");
  return token ? { "Authorization": `Bearer ${token}` } : {};
}

/**
 * Read a cookie value by name (returns null if absent).
 */
function readCookie(name) {
  if (typeof document === "undefined") return null;
  const prefix = `${encodeURIComponent(name)}=`;
  const item = document.cookie.split("; ").find((x) => x.startsWith(prefix));
  return item ? decodeURIComponent(item.slice(prefix.length)) : null;
}

let _csrfFetchPromise = null;

/**
 * Ensure a CSRF cookie is present. Calls /api/auth/csrf if missing.
 * Deduplicates concurrent callers so we only hit the backend once per refresh.
 */
async function ensureCsrfToken() {
  const existing = readCookie("csrf_token");
  if (existing) return existing;
  if (_csrfFetchPromise) return _csrfFetchPromise;

  _csrfFetchPromise = fetch(`${API_BASE}/api/auth/csrf`, {
    credentials: "include",
    headers: getAuthHeaders(),
  })
    .catch(() => null)
    .finally(() => {
      _csrfFetchPromise = null;
    });

  await _csrfFetchPromise;
  return readCookie("csrf_token");
}

/**
 * Make an authenticated fetch request
 * Includes both credentials (cookies) and Authorization header (localStorage token),
 * and injects X-CSRF-Token for unsafe methods as a defense-in-depth (harmless when
 * Authorization: Bearer is present since the middleware skips CSRF in that case).
 */
export async function authFetch(url, options = {}) {
  const method = (options.method || "GET").toUpperCase();

  const headers = {
    ...getAuthHeaders(),
    ...options.headers,
  };

  if (UNSAFE_METHODS.has(method)) {
    const csrf = await ensureCsrfToken();
    if (csrf) {
      headers["X-CSRF-Token"] = csrf;
    }
  }

  return fetch(url, {
    ...options,
    credentials: "include",
    headers,
  });
}

/**
 * Store session token in localStorage (called after successful login)
 */
export function storeSessionToken(token) {
  if (token) {
    localStorage.setItem("session_token", token);
  }
}

/**
 * Clear session token from localStorage (called on logout)
 */
export function clearSessionToken() {
  localStorage.removeItem("session_token");
  // Best-effort clear of the CSRF cookie so the next session gets a fresh one.
  if (typeof document !== "undefined") {
    document.cookie = "csrf_token=; Max-Age=0; path=/; SameSite=None; Secure";
  }
}
